/**
 * @license
 * @copyright Copyright Motili Inc., 2024 All Rights Reserved
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { useCountriesQuery } from 'common/stores/redux/MetaStore/MetaStoreApi';
import { joinDataTestId, LabelWithRequired } from '@motili/ui';

import * as Validator from 'common/utils/Validator';
import FormTextInput from 'common/components/form/FormTextInput';
import { formInputDefaultProps } from 'common/utils/form-input-utils';
import * as TwilioService from '../../services/TwilioService';
import FormSelect from './FormSelect';
import Checkbox from './CheckboxWrapper';

import styles from './FormPhoneInput.module.scss';

const FormPhoneInput = ({
    value,
    phoneCountry,
    isMobile,
    onChange,
    saveUnmasked,
    validation,
    className,
    dataTestId,
    label,
    required,
    disabled,
    flexDirection,
    usePortal = true,
    hideCountrySelection,
    supportExtension,
    extLabel = 'EXTENSION',
    extValue,
    allowTypeOverride = false,
}) => {
    const { data: countries } = useCountriesQuery();
    const countryCodeOptions = countries?.map(c => ({
        id: c.id,
        display: `${c.description} (${c.countryCode})`,
    }));

    const [busy, setBusy] = useState(false);
    const [isValid, setIsValid] = useState(value ? true : null);
    const [errorMessage, setErrorMessage] = useState('');

    const phoneData = useMemo(() => {
        return {
            value,
            isValid,
            phoneCountry,
            isMobile,
            extValue: !isMobile ? extValue || '' : '',
        };
    }, [extValue, isMobile, isValid, phoneCountry, value]);

    const getValue = useCallback(
        inputValue => {
            return saveUnmasked ? unMask(inputValue) : inputValue;
        },
        [saveUnmasked]
    );

    const validatePhone = useCallback(
        (inputValue, country) => {
            if (inputValue) {
                const phoneValidator = saveUnmasked
                    ? Validator.isUnformattedPhone
                    : Validator.isPhone;
                if (phoneValidator(inputValue, country)) {
                    const _service = isMobile
                        ? TwilioService.validateMobile
                        : TwilioService.validatePhone;
                    setBusy(true);
                    _service(inputValue, country)
                        .then(({ valid, message }) => {
                            setIsValid(valid);
                            setErrorMessage(message || '');
                            setBusy(false);
                            if (onChange) {
                                onChange({
                                    ...phoneData,
                                    isValid: valid,
                                });
                            }
                        })
                        .catch(() => {
                            setIsValid(false);
                            setErrorMessage(
                                'Error validating phone number. Please try again'
                            );
                            setBusy(false);
                            if (onChange) {
                                onChange({
                                    ...phoneData,
                                    isValid: false,
                                });
                            }
                        });
                }
            }
        },
        [isMobile, onChange, phoneData, saveUnmasked]
    );

    const onBlur = (...args) => {
        const inputValue = args[0].target.value;
        if (inputValue && !Validator.isPhone(inputValue, phoneCountry)) {
            setIsValid(false);
            if (onChange) {
                onChange({
                    ...phoneData,
                    value: getValue(inputValue),
                    isValid: false,
                });
            }
        } else {
            validatePhone(inputValue, phoneCountry);
        }
    };

    const onChangeInput = (...args) => {
        const inputValue = args[0].target.value;
        if (onChange) {
            onChange({
                ...phoneData,
                value: getValue(inputValue),
            });
        }
    };

    const onChangeCountryCode = (...args) => {
        const country = args[0].id;
        if (onChange) {
            onChange({
                ...phoneData,
                phoneCountry: country,
            });
        }
    };

    const getValidationState = () => {
        if (isValid === true) {
            return { state: 'success' };
        }
        if (isValid === false) {
            const message = errorMessage || validation?.message;
            return { state: 'error', message };
        }
        return undefined;
    };

    useEffect(() => {
        setIsValid(null);
        validatePhone(value, phoneCountry);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, phoneCountry, isMobile]);

    return (
        <div className={className}>
            {label && (
                <LabelWithRequired
                    dataTestId={dataTestId}
                    label={label}
                    required={required}
                />
            )}
            {hideCountrySelection ? (
                <FormTextInput
                    validation={{
                        ...getValidationState(),
                    }}
                    mask='phone'
                    onChange={onChangeInput}
                    onBlur={onBlur}
                    value={value}
                    disabled={disabled || busy}
                    dataTestId={dataTestId}
                />
            ) : (
                <div
                    className={`${styles.flexed} ${
                        flexDirection === 'row'
                            ? styles.rowInput
                            : styles.columnInput
                    }`}
                    data-test-id={joinDataTestId([dataTestId, 'container'])}
                >
                    <div className={styles.input}>
                        <FormSelect
                            options={countryCodeOptions}
                            value={phoneCountry}
                            onChange={onChangeCountryCode}
                            disabled={disabled || busy}
                            menuPortalTarget={usePortal ? document.body : null}
                            menuPosition='absolute'
                            menuShouldScrollIntoView={false}
                            dataTestId={dataTestId}
                        />
                    </div>
                    <div className={styles.input}>
                        <FormTextInput
                            validation={{
                                ...getValidationState(),
                            }}
                            mask='phone'
                            onChange={onChangeInput}
                            onBlur={onBlur}
                            value={value}
                            disabled={disabled || busy}
                            dataTestId={dataTestId}
                        />
                    </div>
                </div>
            )}
            {allowTypeOverride && (
                <Checkbox
                    style={{
                        marginTop: 5,
                    }}
                    label='This is a Landline'
                    onChange={() => {
                        if (onChange) {
                            onChange({
                                ...phoneData,
                                isMobile: !isMobile,
                                extValue: isMobile ? '' : phoneData.extValue,
                            });
                        }
                    }}
                    checked={!isMobile}
                    disabled={disabled || busy}
                />
            )}
            {supportExtension && !isMobile && (
                <FormTextInput
                    style={{
                        marginTop: 10,
                    }}
                    label={extLabel}
                    value={extValue}
                    disabled={disabled || busy}
                    onChange={e => {
                        const ext = e?.target?.value;
                        if (ext && !Validator.isPhoneExt(ext)) {
                            return;
                        }

                        if (onChange) {
                            onChange({
                                ...phoneData,
                                extValue: ext,
                            });
                        }
                    }}
                />
            )}
        </div>
    );
};

FormPhoneInput.defaultValidation = {
    message: 'Invalid',
};

const unMask = value => {
    return value.replace(/\D/g, '');
};

FormPhoneInput.propTypes = {
    ...FormTextInput.propTypes,
    isMobile: PropTypes.bool,
    phoneCountry: PropTypes.string,
    required: PropTypes.bool,
    className: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    validation: PropTypes.shape({
        message: PropTypes.string,
    }),
    disabled: PropTypes.bool,
    flexDirection: PropTypes.string,
    usePortal: PropTypes.bool,
    hideCountrySelection: PropTypes.bool,
    supportExtension: PropTypes.bool,
    extValue: PropTypes.string,
    extLabel: PropTypes.string,
    allowTypeOverride: PropTypes.bool,
};

FormPhoneInput.defaultProps = {
    ...formInputDefaultProps,
    isMobile: false,
    phoneCountry: 'US',
    hideCountrySelection: false,
};

export default FormPhoneInput;
